import { computed, ComputedRef, Ref } from "vue";

import type { Daytime } from "@ctrl/controller-rpc";

import { compareDaytime, isMidnightEndDaytime } from "@/components/dates";

export interface TimeOption {
  label: string;
  value: Daytime;
}

export function useWorkingHours(
  locale: ComputedRef<{ formatDaytime: (daytime: Daytime) => string }>,
  startWorkingHour: Ref<Daytime>,
  endWorkingHour: Ref<Daytime>,
  is24hFormat: Ref<boolean>
) {
  // Helper function to convert Daytime to unique key
  const daytimeToKey = (daytime: Daytime) => {
    return `${daytime.hours}:${daytime.minutes || 0}:${daytime.seconds || 0}`;
  };

  const formatTime = (daytime: Daytime) => {
    if (is24hFormat.value) {
      return locale.value.formatDaytime(daytime);
    }
    const hours = daytime.hours;
    const minutes = daytime.minutes || 0;
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const displayMinutes = minutes.toString().padStart(2, "0");
    return `${displayHours}:${displayMinutes} ${period}`;
  };

  // Generate on-the-hour time options from 00:00 to 23:00.
  const generateTimeOptions = (): TimeOption[] => {
    const options: TimeOption[] = [];
    for (let hours = 0; hours < 24; hours++) {
      const daytime: Daytime = { hours, minutes: 0, seconds: 0 };
      options.push({
        label: formatTime(daytime),
        value: daytime,
      });
    }
    return options;
  };

  const allTimeOptions = computed(() => generateTimeOptions());

  // Midnight as an *end* time (end of the day) is encoded as { hours: 0 } and
  // mapped to 24:00 by the calendar (see isMidnightEndDaytime).
  const isMidnightEnd = isMidnightEndDaytime;

  const startTimeOptions = computed(() => {
    // When the end is midnight (24:00) every start time is valid.
    if (isMidnightEnd(endWorkingHour.value)) return allTimeOptions.value;
    return allTimeOptions.value.filter(
      (opt) => compareDaytime(opt.value, endWorkingHour.value) < 0
    );
  });
  const endTimeOptions = computed(() => {
    const options = allTimeOptions.value.filter(
      (opt) => compareDaytime(opt.value, startWorkingHour.value) > 0
    );
    // Offer midnight (end of day) as the latest selectable end time.
    options.push({
      label: is24hFormat.value ? "24:00" : "12:00 AM",
      value: { hours: 0, minutes: 0, seconds: 0 },
    });
    return options;
  });

  // Find matching options for v-select
  const selectedStartOption = computed(() => {
    return startTimeOptions.value.find(
      (opt) => daytimeToKey(opt.value) === daytimeToKey(startWorkingHour.value)
    );
  });

  const selectedEndOption = computed(() => {
    return endTimeOptions.value.find(
      (opt) => daytimeToKey(opt.value) === daytimeToKey(endWorkingHour.value)
    );
  });

  const editStartWorkingHour = (value: Daytime) => {
    startWorkingHour.value = value;
    // Midnight end (24:00) is always after any start, so never bump it.
    if (isMidnightEnd(endWorkingHour.value)) return;
    if (compareDaytime(startWorkingHour.value, endWorkingHour.value) > 0) {
      endWorkingHour.value = {
        hours: startWorkingHour.value.hours + 1,
        minutes: startWorkingHour.value.minutes || 0,
        seconds: startWorkingHour.value.seconds || 0,
      };
    }
  };

  const editEndWorkingHour = (value: Daytime) => {
    endWorkingHour.value = value;
    // Midnight is the latest possible end; never pull the start back.
    if (isMidnightEnd(value)) return;
    if (compareDaytime(startWorkingHour.value, endWorkingHour.value) > 0) {
      startWorkingHour.value = {
        hours: Math.max(endWorkingHour.value.hours - 1, 0),
        minutes: endWorkingHour.value.minutes || 0,
        seconds: endWorkingHour.value.seconds || 0,
      };
    }
  };

  return {
    startTimeOptions,
    endTimeOptions,
    selectedStartOption,
    selectedEndOption,
    editStartWorkingHour,
    editEndWorkingHour,
  };
}
